import React from 'react'
import { Container, Card } from 'react-bootstrap'
import { CardRowItem } from 'application/components/cardRowItem'
import * as S from './proposalElements'
import * as E from './deductionSectionElements'
import * as P from './../pdf/pdfElements'
import { useSelector } from 'react-redux'
import {
  formatCurrency,
  IsSpecialEmployer,
  payFrequencyToPaysPA,
  useWindowSize,
} from 'application/helpers'
import { ColoredBlock } from '@mmsg/louie'
import { MonthlySalaryPackagingDeductions, IsExGST } from './proposalFunctions'
import { getCurrentResolutionBreakPoint } from 'application/utils/utilityFunctions'
import { IProposalDeductionSectionProps } from './proposalInterfaces/deductionSectionProps'
import { ITheme } from 'application/utils/type'
import globals from 'application/constants/globals'

interface IStateUpdatedProps {
  individualEstimate: { estimate: { updatedOn: string; id: string } }
}

interface IInputProps {
  data: IProposalDeductionSectionProps
  userEmployer: {
    name: string
    employerGRS?: {
      gstExclusiveDeductionsCode?: string
      displayInterestRateOnDigitalEstimate: boolean
    }
  }
  downloadingPdf?: boolean
  isAnnual?: boolean
  pdf?: boolean
  title: string
  theme: ITheme
  proposalEstimateId: string
}

interface ILocalCardItemProps {
  title: string
  subtitle?: string | null
  inclGSTValue: number
  exclGSTValue: number
  textValue?: string | null
  id: string
}

const DeductionSection = (props: IInputProps) => {
  const [width] = useWindowSize()
  const resolutionBreakpoint = {
    nonDesktop: getCurrentResolutionBreakPoint(false),
  }

  let isSAGBYO = IsSpecialEmployer('SAGBYO') 
  let isACTGO = IsSpecialEmployer('ACTGO') 

  const TotalSalaryPackingDeductionsBlock = () => {
    let isMobile = width < resolutionBreakpoint.nonDesktop
    if (isMobile) {
      return (
        <S.DeductionTotalContainerMobile theme={props.theme}>
          <S.DeductionColorBlockMobile theme={props.theme}>
            <h2 className="mb-2 text-medium">Estimated out of pocket</h2>
            <b id="deductionsection.outofpocket">
              {formatCurrency(props.data.outOfPocketPerPay)}
            </b>
          </S.DeductionColorBlockMobile>
          <S.BrandDefaultText className="text-center mt-2">
            <div>Total Salary Packaging Deductions</div>
            <b id="deductionsection.monthlydeductions">
              {formatCurrency(
                MonthlySalaryPackagingDeductions(isExGST, props.data)
              )}
            </b>
          </S.BrandDefaultText>
          <E.TaxMedicareSavingDiv>
            Less estimated Tax and Medicare Levy Savings
          </E.TaxMedicareSavingDiv>
          {props.data.taxandMedicareSavingsLifeofLease > 0 && <b className='text-center'>-{formatCurrency(props.data.taxandMedicareSavingsLifeofLease / props.data.leaseTerm * 12 / payFrequencyToPaysPA(props.data.payFrequency))}</b>}          
        </S.DeductionTotalContainerMobile>
      )
    } //desktop
    else
      return (
        <S.DeductionTotalContainer>
          <div>
            <S.DeductionTotalBlock theme={props.theme} primary>
              <div>Total Salary Packaging Deductions</div>
              <b id="deductionsection.monthlydeductions" className='text-right'>
                {formatCurrency(
                  MonthlySalaryPackagingDeductions(isExGST, props.data)
                )}
              </b>
            </S.DeductionTotalBlock>
            <S.DeductionTotalBlock theme={props.theme} primary={false}>
              <div>Less estimated Tax and Medicare Levy Savings</div>
              {props.data.taxandMedicareSavingsLifeofLease > 0 && <b className='text-right'>-{formatCurrency(props.data.taxandMedicareSavingsLifeofLease / props.data.leaseTerm * 12 / payFrequencyToPaysPA(props.data.payFrequency))}</b>}
            </S.DeductionTotalBlock>
          </div>
          <E.ColouredBlock>
            {' '}
            <h2 className="mb-2">Estimated out of pocket</h2>
            <b id="deductionsection.outofpocket">
              {formatCurrency(props.data.outOfPocketPerPay)}
            </b>
          </E.ColouredBlock>
        </S.DeductionTotalContainer>
      )
  }

  //Variable FINANCED PAYMENTS subtext
  const input = [`Includes the car on roads`]
  if (
    props.userEmployer &&
    props.userEmployer.employerGRS &&
    props.userEmployer.employerGRS.displayInterestRateOnDigitalEstimate
  )
    input.push(
      `${
        props.data && props.data.annualRate ? props.data.annualRate + '% ' : ''
      }interest`
    )

  if (props.data && props.data.financierEstablishmentFee)
    input.push(`Financier Fees`)

  if (
    props.data.leaseProtectionPremium +
      props.data.rpcPremium +
      props.data.extendedWarrantyPremium +
      props.data.gapCoverPremium >
    0
  )
    input.push('add-on lease insurances')

  const last = input.pop() //If nothing else pushed on, this will be the 'Includes the car on roads', and input will be empty.
  let financedPaymentsTxt =
    input.join(', ') + (input.length > 0 ? ' and ' : '') + last + '.'

  let replacementTyresTxt =
    ((globals.isBrandMX && !isSAGBYO) ? props.data.noOfTyresBudget + ' ' : '') +
    'Replacement tyres'

  let isExGST = IsExGST(props.userEmployer)

  let data = {
    carRepaymentExGST: props.isAnnual
      ? props.data.carRepaymentPerAnnumExGST
      : props.data.carRepaymentPerPayExGST,
    carRepaymentIncGST: props.isAnnual
      ? props.data.carRepaymentPerAnnumIncGST
      : props.data.carRepaymentPerPayIncGST,
    fuelExGST: props.isAnnual
      ? props.data.fuelPerAnnumExGST
      : props.data.fuelPerPayExGST,
    fuelIncGST: props.isAnnual
      ? props.data.fuelPerAnnumIncGST
      : props.data.fuelPerPayIncGST,
    electricityExGST: props.isAnnual
      ? props.data.electricityPerAnnumExGST
      : props.data.electricityPerPayExGST,
    electricityIncGST: props.isAnnual
      ? props.data.electricityPerAnnumIncGST
      : props.data.electricityPerPayIncGST,
    maintenanceExGST: props.isAnnual
      ? props.data.maintenancePerAnnumExGST
      : props.data.maintenancePerPayExGST,
    maintenanceIncGST: props.isAnnual
      ? props.data.maintenancePerAnnumIncGST
      : props.data.maintenancePerPayIncGST,
    tyresExGST: props.isAnnual
      ? props.data.tyresPerAnnumExGST
      : props.data.tyresPerPayExGST,
    tyresIncGST: props.isAnnual
      ? props.data.tyresPerAnnumIncGST
      : props.data.tyresPerPayIncGST,
    incidentalsExGST: props.isAnnual
      ? props.data.incidentalsPerAnnumExGST
      : props.data.incidentalsPerPayExGST,
    incidentalsIncGST: props.isAnnual
      ? props.data.incidentalsPerAnnumIncGST
      : props.data.incidentalsPerPayIncGST,
    roadsideExGST: props.isAnnual
      ? props.data.roadsidePerAnnumExGST
      : props.data.roadsidePerPayExGST,
    roadsideIncGST: props.isAnnual
      ? props.data.roadsidePerAnnumIncGST
      : props.data.roadsidePerPayIncGST,
    regoExGST: props.isAnnual
      ? props.data.regoPerAnnumExGST
      : props.data.regoPerPayExGST,
    regoIncGST: props.isAnnual
      ? props.data.regoPerAnnumIncGST
      : props.data.regoPerPayIncGST,
    compInsExGST: props.isAnnual
      ? props.data.compInsPerAnnumExGST
      : props.data.compInsPerPayExGST,
    compInsIncGST: props.isAnnual
      ? props.data.compInsPerAnnumIncGST
      : props.data.compInsPerPayIncGST,
    nlFeeExGST: props.isAnnual
      ? props.data.nlFeePerAnnumExGST
      : props.data.nlFeePerPayExGST,
    nlFeeIncGST: props.isAnnual
      ? props.data.nlFeePerAnnumIncGST
      : props.data.nlFeePerPayIncGST,
    totalPackageIncGST: props.isAnnual
      ? props.data.totalPackagePerAnnumIncGST
      : props.data.totalPackagePerPayIncGST,
    totalPackageExGST: props.isAnnual
      ? props.data.totalPackagePerAnnumExGST
      : props.data.totalPackagePerPayExGST,
    spFeeExGST: props.isAnnual
      ? props.data.spFeePerAnnumExGST
      : props.data.spFeePerPayExGST,
    spFeeIncGST: props.isAnnual
      ? props.data.spFeePerAnnumIncGST
      : props.data.spFeePerPayIncGST,
    fbt: props.isAnnual ? props.data.fbtPerAnnum : props.data.fbtPerPay,
    lcda: props.isAnnual ? props.data.lcdaPerAnnum : props.data.lcdaPerPay,
    greenfleet: props.isAnnual
      ? props.data.greenfleetPerAnnum
      : props.data.greenfleetPerPay,
    gstOnECM: props.isAnnual
      ? props.data.gstOnECMPerAnnum
      : props.data.gstOnECMPerPay,
    erShare: props.isAnnual
      ? props.data.erSharePerAnnum
      : props.data.erSharePerPay,
    erAdminFee: props.isAnnual
      ? props.data.erAdminFeePerAnnum
      : props.data.erAdminFeePerPay,
  }

  let estimateId = useSelector(
    (state: IStateUpdatedProps) => state.individualEstimate.estimate.id
  )

  let indicationOfCapEstimateOrProposalTxt =
    estimateId === props.proposalEstimateId ? 'Proposal' : 'Estimate'

  let financedPaymentsSubTxt: string
  if (estimateId === props.proposalEstimateId && !props.data.isGov)
    financedPaymentsSubTxt =
      "*Any applicable fees will be set out on the financier's 'Lease Schedule', you will be required to sign."
  let isTaxShare = props.data.taxShare.toLowerCase() === 'yes'

  let fuelUseSubtext =
    (props.data.isGov || isSAGBYO || isACTGO) && props.data.vehicle.id
      ? `(${props.data.vehicle.fuelConsumption} litres/100kms${
          props.data.fuelPricePerLitre &&
          props.data.fuelPricePerLitre > 0
            ? ' @ $' + props.data.fuelPricePerLitre + ' per litre)'
            : ')'
        }  (${props.data.vehicle.fuelType})`
      : null

  let electricityUseSubtext =
    (props.data.isGov || isSAGBYO || isACTGO) &&
    props.data.electricityCostPerKm &&
    props.data.electricityCostPerKm > 0
      ? 'Estimated at ' +
        (props.data.electricityCostPerKm * 100).toFixed(1) +
        'c per km'
      : null

  let maintenanceSubtext = (props.data.isGov || isSAGBYO || isACTGO)
    ? "(As per manufacturer's scheduled logbook servicing)"
    : null

  let SAG_cost = ''
  if(isSAGBYO || isACTGO) {
    let costPerTyre = props.data.tyresPerAnnumIncGST * props.data.leaseTermYears / props.data.noOfTyresBudget
    SAG_cost = ` at ${formatCurrency(costPerTyre)} per tyre`
  }
  let replacementTyresSubtext = (props.data.isGov || isSAGBYO || isACTGO) ? `(${props.data.noOfTyresBudget} budgeted for term of lease${SAG_cost})`: null

  let incidentalsSubtext = (props.data.isGov || isSAGBYO || isACTGO)
    ? '(Car washes, detailing etc)'
    : null

  let registrationSubtext = (props.data.isGov || isSAGBYO || isACTGO)
    ? `(${props.data.leaseTermYears - 1} renewals budgeted for term of lease)`
    : null

  let compInsuranceText =
    data.compInsIncGST > 0 ? 'Comprehensive insurance renewal' : ''

  // compInsuranceProvider is Allianz for brand-arranged, other if client arranged.
  // compInsIncGST can be non-zero for both cases.
  let compInsuranceSubText =
    props.data.compInsuranceProvider === 'Allianz' &&
    (props.data.isGov || globals.isBrandMX)
      ? ' (' + globals.brand + ' arranged)'
      : ''

  const displayGstOnECM = () => {
    if (!isExGST) return false

    if (
      props.data.fbtMethod &&
      props.data.fbtMethod.toLowerCase().includes('ecm')
    )
      return true
    if (
      props.data.overTheCapMethod &&
      props.data.overTheCapMethod.toLowerCase().includes('ecm')
    )
      return true

    return false
  }

  const LocalCardItem = ({
    title,
    subtitle,
    inclGSTValue,
    exclGSTValue,
    textValue,
    id,
  }: ILocalCardItemProps) => {
    if (globals.isBrandRS || isSAGBYO || isACTGO)
      return (
        <CardRowItem
          title={title}
          subtitle={subtitle}
          smallSubtitle
          inclGST={inclGSTValue}
          exclGST={exclGSTValue}
          isCurrency="true"
          hideIfNoValue="true"
          id={id}
        />
      )

    if (
      props.userEmployer &&
      props.userEmployer.employerGRS &&
      props.userEmployer.employerGRS.gstExclusiveDeductionsCode !== 'No'
    )
      return (
        <CardRowItem
          title={title}
          subtitle={subtitle}
          smallSubtitle
          value={textValue === undefined ? exclGSTValue : textValue}
          isCurrency={textValue === undefined}
          hideIfNoValue="true"
          id={id}
        />
      )

    if (
      props.userEmployer &&
      props.userEmployer.employerGRS &&
      props.userEmployer.employerGRS.gstExclusiveDeductionsCode === 'No'
    )
      return (
        <>
          <CardRowItem
            title={title}
            value={textValue === undefined ? inclGSTValue : textValue}
            isCurrency={textValue === undefined ? 'true' : 'false'}
            hideIfNoValue="true"
            id={id}
          />
        </>
      )
    return <></>
  }

  const CardBody = () => {
    let isNSWHN = IsSpecialEmployer('NSWHN')
    let divId = ''
    let fbtText = null
    if (isNSWHN) {
      const specialEmployerNameList = [
        'DHNSW',
        'HNHSS',
        'CINSW',
        'HNHET',
        'NSW Ministry of Health',
        'Health System Support Group',
        'Cancer Institute NSW',
        'Health Education and Training Institute',
      ] //different text for employers whose name starts with any of these
      var result = specialEmployerNameList.filter((option) =>
        props.userEmployer.name.startsWith(option)
      )
      let specialEmployer = result.length !== 0

      if (props.data.fbtMethod === 'ECM') {
        divId = 'deductionsection.nswhnecm'
        fbtText =
          'This ' +
          indicationOfCapEstimateOrProposalTxt +
          ' is structured using the Employee Contribution Method (ECM), based on 20% statutory percentage.' +
          (specialEmployer
            ? ''
            : ' This is on top of your current capping limit of $9,010.')
      } else if (props.data.fbtMethod === 'FBT') {
        divId = 'deductionsection.nswhnfbt'
        fbtText = specialEmployer
          ? null
          : 'This ' +
            indicationOfCapEstimateOrProposalTxt +
            ' assumes that you are exempt from Fringe Benefits Tax and you are entitled to a capping limit of $9,010. It is structured to include all or part of your novated lease running costs within your capping limit.'
      } else if (
        props.data.fbtMethod &&
        props.data.fbtMethod.toLowerCase().includes('cap')
      ) {
        divId = 'deductionsection.nswhncap'
        fbtText = specialEmployer
          ? null
          : 'This ' +
            indicationOfCapEstimateOrProposalTxt +
            ' assumes that you are exempt from Fringe Benefits Tax and you are entitled to a capping limit of $9010.00. It is structured to include all or part novated lease and running costs within your capping limit.'
      }
    }

    return (
      <>
        <Card.Body>
          {width >= resolutionBreakpoint.nonDesktop && (globals.isBrandRS || isSAGBYO  || isACTGO) && (
            <E.ParentRowDiv>
              <E.EmptyTextDiv />
              <E.TitlesDiv>
                <E.TitleDiv>Ex GST</E.TitleDiv>
                <E.TitleDiv>Inc GST</E.TitleDiv>
              </E.TitlesDiv>
            </E.ParentRowDiv>
          )}
          <S.CardHeader className={globals.isBrandRS ? 'mb-3' : ''}>
            FINANCED PAYMENTS
          </S.CardHeader>

          <LocalCardItem
            title={financedPaymentsTxt}
            subtitle={financedPaymentsSubTxt}
            inclGSTValue={data.carRepaymentIncGST}
            exclGSTValue={data.carRepaymentExGST}
            id="deductionsection.repaymentsperpay"
          />
          <hr></hr>
          <S.CardHeader className={globals.isBrandRS ? 'mb-3' : ''}>
           {(isSAGBYO ? 'Operating ': 'Running ') + 'Costs & Servicing'}
          </S.CardHeader>
          <LocalCardItem
            title="Fuel"
            subtitle={fuelUseSubtext}
            inclGSTValue={data.fuelIncGST}
            exclGSTValue={data.fuelExGST}
            id="deductionsection.fuelperpay"
          />
          <LocalCardItem
            title="Electricity"
            subtitle={electricityUseSubtext}
            inclGSTValue={data.electricityIncGST}
            exclGSTValue={data.electricityExGST}
            id="deductionsection.electricityperpay"
          />
          <LocalCardItem
            title="Scheduled servicing"
            subtitle={maintenanceSubtext}
            inclGSTValue={data.maintenanceIncGST}
            exclGSTValue={data.maintenanceExGST}
            id="deductionsection.maintenanceperpay"
          />
          <LocalCardItem
            title={replacementTyresTxt}
            subtitle={replacementTyresSubtext}
            inclGSTValue={data.tyresIncGST}
            exclGSTValue={data.tyresExGST}
            id="deductionsection.tyresperpay"
          />
          <LocalCardItem
            title="Incidentals"
            subtitle={incidentalsSubtext}
            inclGSTValue={data.incidentalsIncGST}
            exclGSTValue={data.incidentalsExGST}
            id="deductionsection.incidentalsperpay"
          />
          {props.data.roadsideAssist && (
            <LocalCardItem
              title="Roadside assistance"
              textValue={
                props.data.roadsidePerPayIncGST === 0 ? 'Free' : undefined
              }
              inclGSTValue={data.roadsideIncGST}
              exclGSTValue={data.roadsideExGST}
              id="deductionsection.roadsideassistperpay"
            />
          )}
          <hr></hr>
          <S.CardHeader className={globals.isBrandRS ? 'mb-3' : ''}>
            Registration & Insurance
          </S.CardHeader>
          <LocalCardItem
            title="Registration renewal"
            subtitle={registrationSubtext}
            inclGSTValue={data.regoIncGST}
            exclGSTValue={data.regoExGST}
            id="deductionsection.registrationperpay"
          />
          {data.compInsIncGST > 0 && (
            <LocalCardItem
              title={compInsuranceText}
              subtitle={compInsuranceSubText}
              inclGSTValue={data.compInsIncGST}
              exclGSTValue={data.compInsExGST}
              id="deductionsection.compinsuranceperpay"
            />
          )}
          {props.data.nlFeePerPayIncGST + props.data.spFeePerPayIncGST > 0 && (
            <div>
              <hr></hr>
              <S.CardHeader className={globals.isBrandRS ? 'mb-3' : ''}>
                Novated lease fees & charges
              </S.CardHeader>
              <LocalCardItem
                title={globals.brand + ' novated leasing fee'}
                inclGSTValue={data.nlFeeIncGST}
                exclGSTValue={data.nlFeeExGST}
                id="deductionsection.nlfeeperpayincgst"
              />
              <LocalCardItem
                title="Salary packaging fee"
                inclGSTValue={data.spFeeIncGST}
                exclGSTValue={data.spFeeExGST}
                id="deductionsection.spfeeperpayincgst"
              />
            </div>
          )}
          {
            props.data.fbtPerPay +
            (displayGstOnECM() ? props.data.gstOnECMPerPay : 0) +
            props.data.lcdaPerPay +
            props.data.greenfleetPerPay +
            (isTaxShare ? props.data.erSharePerPay : 0) +
            props.data.erAdminFeePerPay !==
            0 && (
            <div>
              <hr></hr>
              <S.CardHeader className="">
                Tax Implications & other charges
              </S.CardHeader>
              <div className="pt-2 pb-2">
                {fbtText !== null && (
                  <div id={divId}>
                    <b>Please note: </b>
                    {fbtText}
                  </div>
                )}
              </div>

              <LocalCardItem
                title="FBT"
                inclGSTValue={data.fbt}
                exclGSTValue={data.fbt}
                id="deductionsection.fbtperpay2"
              />
              {displayGstOnECM() && (
                <LocalCardItem
                  title="GST on ECM"
                  inclGSTValue={data.gstOnECM}
                  exclGSTValue={data.gstOnECM}
                  id="deductionsection.gstonecmperpay"
                />
              )}

              <LocalCardItem
                title="Luxury car depreciation allowance"
                exclGSTValue={data.lcda}
                inclGSTValue={data.lcda}
                id="deductionsection.lcdaperpay2"
              />
              <LocalCardItem
                title="Greenfleet donation"
                exclGSTValue={props.data.greenfleetPerPay}
                inclGSTValue={props.data.greenfleetPerPay}
                id="deductionsection.greenfleetperpay2"
              />
              {isTaxShare && (
                <LocalCardItem
                  title="Employer share of benefit"
                  exclGSTValue={props.data.erSharePerPay}
                  inclGSTValue={props.data.erSharePerPay}
                  id="deductionsection.ershareperpay2"
                />
              )}
              <LocalCardItem
                title="Employer admin fee"
                exclGSTValue={props.data.erAdminFeePerPay}
                inclGSTValue={props.data.erAdminFeePerPay}
                id="deductionsection.eradminfeeperpay2"
              />
            </div>
          )}
        </Card.Body>
        {!props.isAnnual && <TotalSalaryPackingDeductionsBlock />}
      </>
    )
  }

  return (
    <div className="card-section print-page-break-before" id="deductionsection">
      <Container className="section mt-4">
        {!props.pdf && (
          <Card className="mb-13rem card-shadow">
            <Card.Header className="border-bottom-0">
              <S.CardHeader id="deductionsection.title">
                {props.title}
              </S.CardHeader>
            </Card.Header>
            <CardBody />
          </Card>
        )}

        {props.pdf && (
          <P.StyledCard>
            <P.StyledCardHeader>
              <P.StyledCardHeaderText id="deductionsection.title">
                {props.title}
              </P.StyledCardHeaderText>
            </P.StyledCardHeader>
            <CardBody />
          </P.StyledCard>
        )}
      </Container>
    </div>
  )
}

export default DeductionSection
